import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Invoice, InvoiceStatus, InvoiceSection, InvoicePosition,
  Client, Project, Offer, CreateInvoiceRequest, UpdateInvoiceRequest,
  InvoiceAuditEntry,
} from '../types';
import invoiceService from '../services/invoiceService';
import recurringInvoiceService from '../services/recurringInvoiceService';
import clientService, { composeRecipientAddress } from '../services/clientService';
import projectService from '../services/projectService';
import offerService from '../services/offerService';
import { PageHeader, Badge, Spinner, ModalOverlay } from '../components/ui';
import BusinessAreaPicker from '../components/BusinessAreaPicker';
import { usePageTitle } from '../hooks/usePageTitle';

const statusVariant = (s: InvoiceStatus): 'green' | 'amber' | 'purple' | 'red' => {
  switch (s) {
    case InvoiceStatus.DRAFT: return 'amber';
    case InvoiceStatus.SENT: return 'purple';
    case InvoiceStatus.PAID: return 'green';
    case InvoiceStatus.OVERDUE: return 'red';
    case InvoiceStatus.CANCELLED: return 'red';
  }
};

const statusLabel = (s: InvoiceStatus): string => ({
  [InvoiceStatus.DRAFT]: 'Entwurf',
  [InvoiceStatus.SENT]: 'Versendet',
  [InvoiceStatus.PAID]: 'Bezahlt',
  [InvoiceStatus.OVERDUE]: 'Überfällig',
  [InvoiceStatus.CANCELLED]: 'Storniert',
}[s]);

const allowedTransitions = (s: InvoiceStatus): InvoiceStatus[] => {
  switch (s) {
    case InvoiceStatus.DRAFT: return [InvoiceStatus.SENT, InvoiceStatus.CANCELLED];
    case InvoiceStatus.SENT: return [InvoiceStatus.PAID, InvoiceStatus.OVERDUE];
    case InvoiceStatus.OVERDUE: return [InvoiceStatus.PAID];
    default: return [];
  }
};

const emptySection = (sortOrder: number): InvoiceSection => ({
  sortOrder,
  title: 'Leistungen',
  subtitle: null,
  positions: [],
});

const emptyPosition = (sortOrder: number, number: string): InvoicePosition => ({
  sortOrder,
  positionNumber: number,
  title: '',
  description: '',
  unitPriceCents: 0,
  quantity: 1,
});

const formatDateTime = (iso?: string | null): string =>
  iso ? new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const auditLabel = (action: string): string => {
  if (action.startsWith('STATUS_')) return `Status → ${action.slice(7)}`;
  return ({
    CREATED: 'Angelegt',
    UPDATED: 'Bearbeitet',
    LOCKED: 'Festgeschrieben',
    EMAILED: 'Per E-Mail verschickt',
    CANCELLED: 'Storniert',
    DELETED: 'Gelöscht',
  } as Record<string, string>)[action] ?? action;
};

const todayIso = (): string => new Date().toISOString().slice(0, 10);
const plusDaysIso = (days: number): string => {
  const d = new Date(); d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const emptyInvoice = (): Invoice => ({
  clientId: 0,
  number: '',
  title: '',
  invoiceDate: todayIso(),
  dueDate: plusDaysIso(14),
  status: InvoiceStatus.DRAFT,
  vatRatePercent: 19,
  introText: 'Wir berechnen Ihnen für die aufgeführten Leistungen den ausgewiesenen Betrag:',
  paymentTerms: 'Zahlbar innerhalb von 14 Tagen ohne Abzug.',
  active: true,
  sections: [],
});

const euroInputToCents = (input: string): number => {
  const cleaned = input.replace(/[^0-9,.\-]/g, '').replace(',', '.');
  const value = parseFloat(cleaned);
  if (isNaN(value)) return 0;
  return Math.round(value * 100);
};
const centsToInput = (cents: number): string => (cents / 100).toFixed(2).replace('.', ',');
const formatEuro = (cents: number): string =>
  new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100) + ' EUR';

const InvoiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';

  usePageTitle(isNew ? 'Neue Rechnung' : 'Rechnung bearbeiten');

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [invoice, setInvoice] = useState<Invoice>(emptyInvoice());
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [auditLog, setAuditLog] = useState<InvoiceAuditEntry[]>([]);
  const [sendForm, setSendForm] = useState<{ to: string; cc: string; subject: string; message: string } | null>(null);
  const [sending, setSending] = useState(false);

  // Ab dem Versand ist die Rechnung festgeschrieben: nur noch lesbar (GoBD).
  const locked = !!invoice.lockedAt;

  useEffect(() => {
    (async () => {
      try {
        const clientList = await clientService.getAll();
        setClients(clientList);
        if (!isNew && id) {
          const loaded = await invoiceService.getById(Number(id));
          setInvoice(loaded);
          invoiceService.auditLog(Number(id)).then(setAuditLog).catch(() => setAuditLog([]));
        } else if (isNew) {
          try {
            const suggested = await invoiceService.nextNumber();
            setInvoice(o => ({ ...o, number: suggested }));
          } catch { /* fallback: leeres Feld */ }
        }
      } catch (e) {
        toast.error('Daten konnten nicht geladen werden');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew]);

  useEffect(() => {
    if (!invoice.clientId) { setProjects([]); setOffers([]); return; }
    let cancelled = false;
    projectService.getAll(invoice.clientId)
      .then(list => { if (!cancelled) setProjects(list); })
      .catch(() => { if (!cancelled) setProjects([]); });
    offerService.getAll({
      clientId: invoice.clientId,
      projectId: invoice.projectId ?? undefined,
    })
      .then(list => { if (!cancelled) setOffers(list); })
      .catch(() => { if (!cancelled) setOffers([]); });
    return () => { cancelled = true; };
  }, [invoice.clientId, invoice.projectId]);

  const updateInvoice = (patch: Partial<Invoice>) => setInvoice(o => ({ ...o, ...patch }));

  /**
   * Speichert das Vorhaben sofort und für sich.
   *
   * Nicht über das normale Speichern: das schickt die ganze Rechnung per PUT, und den weist der
   * Server bei einer festgeschriebenen zu Recht ab — gerade die will man aber zuordnen können.
   */
  const saveBusinessArea = async (area: string | null) => {
    updateInvoice({ businessArea: area });
    if (!invoice.id) return;
    try {
      await invoiceService.setBusinessArea(invoice.id, area);
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Das Vorhaben ließ sich nicht speichern');
    }
  };

  // Bei Kundenwahl Ansprechpartner + Empfänger-Anschrift aus Stammdaten vorbefüllen,
  // ohne bereits eingetragene Werte zu überschreiben.
  const selectClient = (id: number) => setInvoice(o => {
    const client = clients.find(c => c.id === id);
    return {
      ...o,
      clientId: id,
      projectId: null,
      contactPerson: o.contactPerson?.trim() ? o.contactPerson : (client?.contactPerson ?? o.contactPerson),
      recipientAddress: o.recipientAddress?.trim()
        ? o.recipientAddress
        : (client ? composeRecipientAddress(client) : o.recipientAddress),
    };
  });

  const updateSection = (idx: number, patch: Partial<InvoiceSection>) => {
    setInvoice(o => {
      const sections = [...o.sections];
      sections[idx] = { ...sections[idx], ...patch };
      return { ...o, sections };
    });
  };

  const updatePosition = (sIdx: number, pIdx: number, patch: Partial<InvoicePosition>) => {
    setInvoice(o => {
      const sections = [...o.sections];
      const positions = [...sections[sIdx].positions];
      positions[pIdx] = { ...positions[pIdx], ...patch };
      sections[sIdx] = { ...sections[sIdx], positions };
      return { ...o, sections };
    });
  };

  const addSection = () => {
    setInvoice(o => ({ ...o, sections: [...o.sections, emptySection(o.sections.length + 1)] }));
  };

  const removeSection = (idx: number) => {
    setInvoice(o => ({ ...o, sections: o.sections.filter((_, i) => i !== idx) }));
  };

  const addPosition = (sIdx: number) => {
    setInvoice(o => {
      const sections = [...o.sections];
      const section = sections[sIdx];
      const nextNumber = `${sIdx + 1}.${section.positions.length + 1}`;
      sections[sIdx] = {
        ...section,
        positions: [...section.positions, emptyPosition(section.positions.length + 1, nextNumber)],
      };
      return { ...o, sections };
    });
  };

  const removePosition = (sIdx: number, pIdx: number) => {
    setInvoice(o => {
      const sections = [...o.sections];
      sections[sIdx] = {
        ...sections[sIdx],
        positions: sections[sIdx].positions.filter((_, i) => i !== pIdx),
      };
      return { ...o, sections };
    });
  };

  const handleSave = async () => {
    if (!invoice.clientId) { toast.error('Bitte Kunden wählen'); return; }
    if (!invoice.number.trim()) { toast.error('Rechnungsnummer fehlt'); return; }
    if (!invoice.title.trim()) { toast.error('Titel fehlt'); return; }

    setSaving(true);
    try {
      const payload: CreateInvoiceRequest | UpdateInvoiceRequest = {
        clientId: invoice.clientId,
        projectId: invoice.projectId ?? null,
        offerId: invoice.offerId ?? null,
        number: invoice.number,
        title: invoice.title,
        contactPerson: invoice.contactPerson ?? null,
        recipientAddress: invoice.recipientAddress ?? null,
        invoiceDate: invoice.invoiceDate,
        dueDate: invoice.dueDate ?? null,
        servicePeriodFrom: invoice.servicePeriodFrom ?? null,
        servicePeriodTo: invoice.servicePeriodTo ?? null,
        introText: invoice.introText ?? null,
        notesHtml: invoice.notesHtml ?? null,
        paymentTerms: invoice.paymentTerms ?? null,
        paymentReference: invoice.paymentReference ?? null,
        alreadyPaid: invoice.alreadyPaid ?? false,
        vatRatePercent: invoice.vatRatePercent,
        notesInternal: invoice.notesInternal ?? null,
        sections: invoice.sections,
        active: invoice.active,
      };
      const saved = isNew
        ? await invoiceService.create(payload)
        : await invoiceService.update(Number(id), payload as UpdateInvoiceRequest);
      toast.success(isNew ? 'Rechnung erstellt' : 'Gespeichert');
      if (isNew) navigate(`/invoices/${saved.id}`);
      else setInvoice(saved);
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? e?.response?.data?.message ?? 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleTransition = async (newStatus: InvoiceStatus) => {
    if (isNew) return;
    if (newStatus === InvoiceStatus.SENT && !window.confirm(
      'Mit dem Versand wird die Rechnung festgeschrieben: Inhalt, Nummer und Beträge sind danach '
      + 'unveränderbar, eine Korrektur ist nur noch per Stornorechnung möglich. Fortfahren?'
    )) return;
    try {
      const updated = await invoiceService.transitionStatus(Number(id), newStatus);
      setInvoice(updated);
      invoiceService.auditLog(Number(id)).then(setAuditLog).catch(() => {});
      toast.success(`Status: ${statusLabel(newStatus)}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? e?.response?.data?.message ?? 'Status-Wechsel fehlgeschlagen');
    }
  };

  const handleImportFromOffer = async () => {
    if (!invoice.offerId) { toast.error('Bitte erst ein Bezugsangebot wählen'); return; }
    const hasPositions = invoice.sections.some(s => s.positions.length > 0);
    if (hasPositions && !window.confirm(
      'Bestehende Positionen werden überschrieben. Fortfahren?'
    )) return;
    try {
      const offer = await offerService.getById(invoice.offerId);
      // Sections + Positions aus Angebot in Rechnungs-Struktur überführen
      const importedSections: InvoiceSection[] = offer.sections.map((os, sIdx) => ({
        sortOrder: os.sortOrder ?? sIdx + 1,
        title: os.title,
        subtitle: os.subtitle ?? null,
        positions: os.positions.map((op, pIdx) => ({
          sortOrder: op.sortOrder ?? pIdx + 1,
          positionNumber: op.positionNumber,
          title: op.title,
          description: op.description ?? null,
          unitPriceCents: op.unitPriceCents,
          quantity: op.quantity,
          unitLabel: op.unitLabel ?? null,
        })),
      }));
      // Übernehme auch sinnvolle Header-Defaults aus dem Angebot, falls noch nicht gesetzt
      updateInvoice({
        sections: importedSections,
        vatRatePercent: invoice.vatRatePercent || offer.vatRatePercent,
        title: invoice.title || offer.title,
        contactPerson: invoice.contactPerson || offer.contactPerson || null,
        recipientAddress: invoice.recipientAddress || offer.recipientAddress || null,
      });
      toast.success(`${importedSections.reduce((n, s) => n + s.positions.length, 0)} Positionen aus Angebot übernommen`);
    } catch (e) {
      toast.error('Übernahme aus Angebot fehlgeschlagen');
    }
  };

  const handleDelete = async () => {
    if (isNew || !id) return;
    if (!window.confirm('Rechnung wirklich löschen?')) return;
    try {
      await invoiceService.delete(Number(id));
      toast.success('Rechnung gelöscht');
      navigate('/invoices');
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const openSendDialog = () => {
    if (isNew || !id) { toast.error('Bitte erst speichern'); return; }
    const client = clients.find(c => c.id === invoice.clientId);
    setSendForm({
      // Rechnungspostfach hat Vorrang, sonst die allgemeine Adresse des Kunden
      to: client?.billingEmail?.trim() || client?.contactEmail || '',
      cc: '',
      subject: `Rechnung ${invoice.number}`,
      message: '',
    });
  };

  const handleSend = async () => {
    if (!sendForm || !id) return;
    if (!sendForm.to.trim()) { toast.error('Bitte einen Empfänger angeben'); return; }
    if (invoice.status === InvoiceStatus.DRAFT && !window.confirm(
      `Rechnung ${invoice.number} an ${sendForm.to.trim()} senden? Damit wird sie zugleich `
      + 'festgeschrieben und ist danach unveränderbar.'
    )) return;

    setSending(true);
    try {
      const updated = await invoiceService.send(Number(id), {
        to: sendForm.to.trim(),
        cc: sendForm.cc.trim() || null,
        subject: sendForm.subject.trim() || null,
        message: sendForm.message.trim() || null,
      });
      setInvoice(updated);
      invoiceService.auditLog(Number(id)).then(setAuditLog).catch(() => {});
      setSendForm(null);
      toast.success(`Rechnung an ${updated.clientName ?? 'den Kunden'} verschickt`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? e?.response?.data?.message ?? 'Versand fehlgeschlagen');
    } finally {
      setSending(false);
    }
  };

  const handleCancelInvoice = async () => {
    if (isNew || !id) return;
    const reason = window.prompt(
      'Stornorechnung erstellen. Die Ursprungsrechnung bleibt unverändert erhalten.\n\nGrund des Stornos:'
    );
    if (reason === null) return;
    if (!reason.trim()) { toast.error('Bitte einen Grund angeben'); return; }
    try {
      const storno = await invoiceService.cancel(Number(id), reason.trim());
      toast.success(`Stornorechnung ${storno.number} erstellt`);
      navigate(`/invoices/${storno.id}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? e?.response?.data?.message ?? 'Storno fehlgeschlagen');
    }
  };

  const handleMakeRecurring = async () => {
    if (isNew || !id) { toast.error('Bitte erst speichern'); return; }
    if (!window.confirm(
      'Aus dieser Rechnung eine Serie anlegen? Positionen, Titel und Zahlungsziel werden '
      + 'übernommen; den nächsten Termin kannst du danach anpassen.'
    )) return;
    try {
      const series = await recurringInvoiceService.createFromInvoice(Number(id));
      toast.success(`Serie „${series.name}" angelegt`);
      navigate('/recurring');
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? 'Serie konnte nicht angelegt werden');
    }
  };

  const handlePreview = async () => {
    if (isNew) { toast.error('Bitte erst speichern'); return; }
    try {
      const html = await invoiceService.renderHtml(Number(id));
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Vorschau konnte nicht geladen werden');
    }
  };

  const handleDownloadPdf = async () => {
    if (isNew) { toast.error('Bitte erst speichern'); return; }
    try {
      const blob = await invoiceService.renderPdf(Number(id));
      const url = URL.createObjectURL(blob);
      const safeNumber = (invoice.number || String(id)).replace(/[^A-Za-z0-9._-]/g, '_');
      const a = document.createElement('a');
      a.href = url;
      a.download = `Rechnung-${safeNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('PDF konnte nicht erstellt werden');
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Spinner /></div>;

  return (
    <div className="p-6 sm:p-8 max-w-6xl mx-auto">
      <PageHeader
        title={isNew ? 'Neue Rechnung' : `Rechnung ${invoice.number}`}
        subtitle={invoice.title}
        action={
          <div className="flex flex-wrap gap-2 items-center">
            <Badge variant={statusVariant(invoice.status)}>{statusLabel(invoice.status)}</Badge>
            {!isNew && allowedTransitions(invoice.status).map(s => (
              <button
                key={s}
                onClick={() => handleTransition(s)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border"
                style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)', background: 'var(--surface-bg)' }}
              >
                → {statusLabel(s)}
              </button>
            ))}
          </div>
        }
      />

      {locked && (
        <div className="mb-6 p-4 rounded-xl border flex flex-wrap gap-3 justify-between items-start"
          style={{ borderColor: 'var(--badge-amber-text)', background: 'var(--badge-amber-bg)' }}>
          <div className="text-sm" style={{ color: 'var(--badge-amber-text)' }}>
            <strong>Festgeschrieben am {formatDateTime(invoice.lockedAt)}</strong>
            <div className="mt-1">
              Versendete Rechnungen sind nach GoBD unveränderbar. Korrekturen laufen über eine
              Stornorechnung; das PDF wird aus der Archivfassung ausgeliefert.
            </div>
            {invoice.documentHash && (
              <div className="mt-1 font-mono text-xs break-all">SHA-256: {invoice.documentHash}</div>
            )}
            {invoice.cancelledByInvoiceNumber && (
              <div className="mt-1">
                Storniert durch{' '}
                <button type="button" className="underline font-semibold"
                  onClick={() => navigate(`/invoices/${invoice.cancelledByInvoiceId}`)}>
                  {invoice.cancelledByInvoiceNumber}
                </button>
              </div>
            )}
            {invoice.cancelsInvoiceNumber && (
              <div className="mt-1">
                Stornorechnung zu{' '}
                <button type="button" className="underline font-semibold"
                  onClick={() => navigate(`/invoices/${invoice.cancelsInvoiceId}`)}>
                  {invoice.cancelsInvoiceNumber}
                </button>
              </div>
            )}
          </div>
          {!invoice.cancelledByInvoiceId && !invoice.cancelsInvoiceId && (
            <button onClick={handleCancelInvoice}
              className="px-4 py-2 rounded-lg text-sm font-semibold border whitespace-nowrap"
              style={{ borderColor: 'var(--badge-red-text)', color: 'var(--badge-red-text)', background: 'var(--surface-bg)' }}>
              Stornorechnung erstellen
            </button>
          )}
        </div>
      )}

      {/* Nach der Festschreibung sperrt das fieldset saemtliche Eingaben. */}
      <fieldset disabled={locked} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
      <Card title="Allgemein">
        <Grid>
          <Field label="Rechnungsnummer *">
            <Input value={invoice.number} onChange={v => updateInvoice({ number: v })} placeholder="2026-R-001" />
          </Field>
          <Field label="Kunde *">
            <Select value={invoice.clientId} onChange={v => selectClient(Number(v))}>
              <option value={0}>— wählen —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Projekt (optional)">
            <Select value={invoice.projectId ?? ''} onChange={v => updateInvoice({ projectId: v === '' ? null : Number(v), offerId: null })}>
              <option value="">— kein Projekt —</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Field>
          <Field label="Bezugsangebot (optional)">
            <div className="flex gap-2">
              <div className="flex-1">
                <Select value={invoice.offerId ?? ''} onChange={v => updateInvoice({ offerId: v === '' ? null : Number(v) })}>
                  <option value="">— kein Bezugsangebot —</option>
                  {offers.map(o => (
                    <option key={o.id} value={o.id}>{o.number} — {o.title}</option>
                  ))}
                </Select>
              </div>
              <button
                type="button"
                onClick={handleImportFromOffer}
                disabled={!invoice.offerId}
                title="Positionen aus dem gewählten Angebot übernehmen (überschreibt bestehende)"
                className="btn-primary bg-brand-600 hover:bg-brand-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ↓ übernehmen
              </button>
            </div>
          </Field>
          <Field label="MwSt-Satz (%)">
            <IntegerInput value={invoice.vatRatePercent} min={0}
              onChange={n => updateInvoice({ vatRatePercent: n })} />
          </Field>
          <Field label="Rechnungsdatum *">
            <Input type="date" value={invoice.invoiceDate} onChange={v => updateInvoice({ invoiceDate: v })} />
          </Field>
          <Field label="Fällig am">
            <Input type="date" value={invoice.dueDate ?? ''} onChange={v => updateInvoice({ dueDate: v || null })} />
          </Field>
          <Field label="Leistungszeitraum von">
            <Input type="date" value={invoice.servicePeriodFrom ?? ''}
              onChange={v => updateInvoice({ servicePeriodFrom: v || null })} />
          </Field>
          <Field label="Leistungszeitraum bis">
            <Input type="date" value={invoice.servicePeriodTo ?? ''}
              onChange={v => updateInvoice({ servicePeriodTo: v || null })} />
          </Field>
          <Field label="Titel / Betreff *" full>
            <Input value={invoice.title} onChange={v => updateInvoice({ title: v })} placeholder="z. B. Entwicklungsleistungen Q1 2026" />
          </Field>
          <Field label="Ansprechpartner (Anrede)">
            <Input value={invoice.contactPerson ?? ''} onChange={v => updateInvoice({ contactPerson: v })} placeholder="Herrn Jakob Seiß" />
          </Field>
          <Field label="Empfänger-Anschrift" full>
            <Textarea value={invoice.recipientAddress ?? ''} rows={4}
              onChange={v => updateInvoice({ recipientAddress: v })}
              placeholder={'Landeskirchenamt\nHerrn Jakob Seiß\nDietrich-Bonhoeffer-Str. 1\n38300 Wolfenbüttel'} />
          </Field>
          <Field label="Einleitungstext" full>
            <Textarea value={invoice.introText ?? ''} rows={3}
              onChange={v => updateInvoice({ introText: v })}
              placeholder="Wir berechnen Ihnen für die aufgeführten Leistungen den ausgewiesenen Betrag:" />
          </Field>
          {/* Bei Vorkasse ausgeblendet statt nur wirkungslos: das Feld ist mit „Zahlbar
              innerhalb von 14 Tagen" vorbelegt, und ein sichtbarer Satz, der nicht auf dem
              Dokument landet, ist schlimmer als kein Feld. Auf einer bezahlten Rechnung steht
              stattdessen der Dank. */}
          {!invoice.alreadyPaid && (
            <Field label="Zahlungsbedingungen" full>
              <Textarea value={invoice.paymentTerms ?? ''} rows={2}
                onChange={v => updateInvoice({ paymentTerms: v })} />
            </Field>
          )}
          {/* Leer lassen ist der Normalfall: dann steht „Rechnung <Nummer>" auf dem Dokument,
              wie bisher. Gebraucht wird das Feld, wenn der Kunde den Verwendungszweck schon
              kennt — etwa aus einer Bestellbestätigung. Zwei verschiedene wären einer zu viel,
              er überweist mit dem, den er zuerst gelesen hat, und dann findet der Kontoabgleich
              die Zahlung nicht. Er sucht nach beidem, aber nur, wenn es hier steht. */}
          {/* Der Regelfall im Verkauf über radsport-events.de: erst das Geld, dann die Rechnung.
              Ohne das Häkchen stünde auf einer nachträglich geschriebenen Rechnung „Bitte
              überweisen Sie…" — das liest sich wie eine Mahnung an jemanden, der längst gezahlt
              hat. Es steuert nur die Texte; bezahlt ist eine Rechnung erst, wenn der
              Kontoauszug sie bestätigt. */}
          <Field label="Zahlung" full>
            <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-primary)' }}>
              <input
                type="checkbox"
                checked={invoice.alreadyPaid ?? false}
                onChange={e => updateInvoice({ alreadyPaid: e.target.checked })}
              />
              Betrag ist bereits eingegangen (Vorkasse)
            </label>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
              Dann steht auf Rechnung und E-Mail ein Dank statt einer Zahlungsaufforderung —
              ohne Zahlungsziel, Zahlungsbedingungen und Kontoangaben.
            </p>
          </Field>
          <Field label="Verwendungszweck (nur wenn abweichend)" full>
            <Input
              value={invoice.paymentReference ?? ''}
              onChange={v => updateInvoice({ paymentReference: v || null })}
              placeholder={`Leer = „Rechnung ${invoice.number || '2026-R-001'}"`} />
          </Field>
        </Grid>
      </Card>

      <Card title="Positionen" actions={
        <button onClick={addSection}
          className="btn-primary bg-brand-600 hover:bg-brand-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
          + Abschnitt
        </button>
      }>
        {invoice.sections.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Noch keine Abschnitte angelegt.</p>
        ) : invoice.sections.map((section, sIdx) => {
          const netCents = section.positions.reduce((sum, p) => sum + p.unitPriceCents * p.quantity, 0);
          const vatCents = Math.round((netCents * invoice.vatRatePercent) / 100);
          const grossCents = netCents + vatCents;
          return (
            <div key={sIdx} className="mb-6 p-4 rounded-lg border"
              style={{ borderColor: 'var(--surface-border)', background: 'var(--surface-bg)' }}>
              <div className="flex flex-wrap gap-3 items-end mb-3">
                <Field label="Abschnittstitel *">
                  <Input value={section.title} onChange={v => updateSection(sIdx, { title: v })}
                    placeholder="Leistungen" />
                </Field>
                <Field label="Untertitel">
                  <Input value={section.subtitle ?? ''} onChange={v => updateSection(sIdx, { subtitle: v })} />
                </Field>
                <button onClick={() => removeSection(sIdx)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold border"
                  style={{ borderColor: 'var(--badge-red-text)', color: 'var(--badge-red-text)' }}>
                  Abschnitt löschen
                </button>
              </div>

              <table className="w-full text-sm mb-3">
                <thead style={{ color: 'var(--text-muted)' }}>
                  <tr className="text-left">
                    <th className="px-2 py-1 w-16">Nr.</th>
                    <th className="px-2 py-1">Bezeichnung</th>
                    <th className="px-2 py-1 w-32">Einzelpreis (EUR)</th>
                    <th className="px-2 py-1 w-20">Anzahl</th>
                    <th className="px-2 py-1 w-28">Einheit</th>
                    <th className="px-2 py-1 w-32 text-right">Summe</th>
                    <th className="px-2 py-1 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {section.positions.map((pos, pIdx) => (
                    <React.Fragment key={pIdx}>
                      <tr className="border-t" style={{ borderColor: 'var(--surface-border)' }}>
                        <td className="px-2 py-1">
                          <Input value={pos.positionNumber} onChange={v => updatePosition(sIdx, pIdx, { positionNumber: v })} />
                        </td>
                        <td className="px-2 py-1">
                          <Input value={pos.title} onChange={v => updatePosition(sIdx, pIdx, { title: v })} placeholder="Leistung" />
                        </td>
                        <td className="px-2 py-1">
                          <PriceInput valueCents={pos.unitPriceCents}
                            onChange={cents => updatePosition(sIdx, pIdx, { unitPriceCents: cents })} />
                        </td>
                        <td className="px-2 py-1">
                          <IntegerInput value={pos.quantity}
                            onChange={n => updatePosition(sIdx, pIdx, { quantity: n })} />
                        </td>
                        <td className="px-2 py-1">
                          <Input value={pos.unitLabel ?? ''} onChange={v => updatePosition(sIdx, pIdx, { unitLabel: v })}
                            placeholder="Std., Stk., …" />
                        </td>
                        <td className="px-2 py-1 text-right font-semibold">
                          {formatEuro(pos.unitPriceCents * pos.quantity)}
                        </td>
                        <td className="px-2 py-1">
                          <button onClick={() => removePosition(sIdx, pIdx)}
                            className="text-xs px-2 py-1 rounded" style={{ color: 'var(--badge-red-text)' }}>
                            ✕
                          </button>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={7} className="px-2 pb-3">
                          <Textarea value={pos.description ?? ''} rows={2}
                            placeholder="Beschreibung (HTML erlaubt)"
                            onChange={v => updatePosition(sIdx, pIdx, { description: v })} />
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>

              <div className="flex justify-between items-center">
                <button onClick={() => addPosition(sIdx)}
                  className="text-xs px-3 py-1.5 rounded-lg border"
                  style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}>
                  + Position
                </button>
                <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
                  Netto: <strong>{formatEuro(netCents)}</strong>
                  {' · '}MwSt {invoice.vatRatePercent}%: <strong>{formatEuro(vatCents)}</strong>
                  {' · '}Brutto: <strong style={{ color: 'var(--text-primary)' }}>{formatEuro(grossCents)}</strong>
                </div>
              </div>
            </div>
          );
        })}
      </Card>

      <Card title="Zusätzliche Hinweise (HTML)">
        <Textarea value={invoice.notesHtml ?? ''} rows={4}
          onChange={v => updateInvoice({ notesHtml: v })} />
      </Card>

      <Card title="Interne Notizen (nicht im PDF)">
        <Textarea value={invoice.notesInternal ?? ''} rows={3}
          onChange={v => updateInvoice({ notesInternal: v })} />
      </Card>
      </fieldset>

      {/* Steht bewusst AUSSERHALB des fieldsets: der Geschäftsbereich ist die einzige Angabe,
          die sich auch an einer festgeschriebenen Rechnung noch ändern lässt. Er steht nicht
          auf dem Dokument und ändert keinen Betrag — ohne diese Ausnahme ließe sich keine
          bereits versendete Rechnung einem Vorhaben zuordnen. Eigener Speicherweg (PATCH), weil
          der normale PUT bei einer festgeschriebenen Rechnung zu Recht abgelehnt wird. */}
      {!isNew && (
        <Card title="Vorhaben (nur intern, nicht im PDF)">
          <BusinessAreaPicker
            id="invoice-business-area"
            value={invoice.businessArea}
            onChange={saveBusinessArea}
          />
          <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
            Wird sofort gespeichert. Dagegen rechnet das Dashboard die Ausgaben desselben
            Vorhabens — zuzuordnen sind die auf der Seite „Konten".
          </p>
        </Card>
      )}

      {!isNew && auditLog.length > 0 && (
        <Card title="Journal">
          <table className="w-full text-sm">
            <thead style={{ color: 'var(--text-muted)' }}>
              <tr className="text-left">
                <th className="px-2 py-1 w-48">Zeitpunkt</th>
                <th className="px-2 py-1 w-40">Vorgang</th>
                <th className="px-2 py-1 w-32">Benutzer</th>
                <th className="px-2 py-1">Detail</th>
              </tr>
            </thead>
            <tbody>
              {auditLog.map(entry => (
                <tr key={entry.id} className="border-t" style={{ borderColor: 'var(--surface-border)' }}>
                  <td className="px-2 py-1 whitespace-nowrap">{formatDateTime(entry.createdAt)}</td>
                  <td className="px-2 py-1 font-semibold">{auditLabel(entry.action)}</td>
                  <td className="px-2 py-1">{entry.actor}</td>
                  <td className="px-2 py-1" style={{ color: 'var(--text-muted)' }}>{entry.detail ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <div className="flex flex-wrap gap-3 justify-end mt-6">
        {!isNew && !locked && (
          <button onClick={handleDelete}
            className="px-4 py-2 rounded-lg text-sm font-semibold border"
            style={{ borderColor: 'var(--badge-red-text)', color: 'var(--badge-red-text)' }}>
            Löschen
          </button>
        )}
        <button onClick={() => navigate('/invoices')}
          className="px-4 py-2 rounded-lg text-sm font-semibold border"
          style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}>
          Zurück
        </button>
        {!isNew && (
          <button onClick={handlePreview}
            className="px-4 py-2 rounded-lg text-sm font-semibold border"
            style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}>
            Vorschau Rechnung
          </button>
        )}
        {!isNew && !invoice.recurringInvoiceId && (
          <button onClick={handleMakeRecurring}
            className="px-4 py-2 rounded-lg text-sm font-semibold border"
            style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}>
            ↻ Serie daraus anlegen
          </button>
        )}
        {!isNew && invoice.status !== InvoiceStatus.CANCELLED && (
          <button onClick={openSendDialog}
            className="px-4 py-2 rounded-lg text-sm font-semibold border"
            style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}>
            ✉ Per E-Mail versenden
          </button>
        )}
        {!isNew && (
          <button onClick={handleDownloadPdf}
            className="px-4 py-2 rounded-lg text-sm font-semibold border"
            style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}>
            PDF herunterladen
          </button>
        )}
        {!locked && (
          <button onClick={handleSave} disabled={saving}
            className="btn-primary bg-brand-600 hover:bg-brand-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? 'Speichert…' : 'Speichern'}
          </button>
        )}
      </div>

      {sendForm && (
        <ModalOverlay
          role="dialog"
          ariaLabelledBy="invoice-send-title"
          onKeyDown={(e) => { if (e.key === 'Escape') setSendForm(null); }}
        >
          <div className="rounded-xl p-6 w-full max-w-lg"
            style={{ background: 'var(--surface-bg)', color: 'var(--text-primary)' }}>
            <h2 id="invoice-send-title" className="text-lg font-semibold mb-1 tracking-tight">
              Rechnung {invoice.number} versenden
            </h2>
            <p className="text-xs mb-5" style={{ color: 'var(--text-muted)' }}>
              Die Rechnung geht als PDF-Anhang von office@gsk-it.de raus.
              {invoice.status === InvoiceStatus.DRAFT
                ? ' Mit dem Versand wird sie festgeschrieben.'
                : ' Sie ist bereits festgeschrieben — das verschickt eine weitere Kopie.'}
            </p>

            <div className="grid grid-cols-1 gap-4">
              <Field label="An *">
                <Input type="email" value={sendForm.to}
                  onChange={v => setSendForm({ ...sendForm, to: v })}
                  placeholder="rechnung@kunde.de" />
              </Field>
              <Field label="Kopie an (CC, mit Komma getrennt)">
                <Input value={sendForm.cc} onChange={v => setSendForm({ ...sendForm, cc: v })} />
              </Field>
              <Field label="Betreff">
                <Input value={sendForm.subject} onChange={v => setSendForm({ ...sendForm, subject: v })} />
              </Field>
              <Field label="Nachricht (leer = Standardanschreiben)">
                <Textarea value={sendForm.message} rows={5}
                  onChange={v => setSendForm({ ...sendForm, message: v })}
                  placeholder="Leer lassen für den Standardtext mit Rechnungsnummer, Betrag und Fälligkeit." />
              </Field>
            </div>

            <div className="flex gap-3 justify-end mt-6">
              <button onClick={() => setSendForm(null)}
                className="px-4 py-2 rounded-lg text-sm font-semibold border"
                style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}>
                Abbrechen
              </button>
              <button onClick={handleSend} disabled={sending}
                className="btn-primary bg-brand-600 hover:bg-brand-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                {sending ? 'Versendet…' : 'Jetzt senden'}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
};

/* === UI Helpers === */

const Card: React.FC<{ title: string; actions?: React.ReactNode; children: React.ReactNode }> = ({ title, actions, children }) => (
  <div className="mb-6 p-5 rounded-xl border" style={{ borderColor: 'var(--surface-border)', background: 'var(--surface-bg-elevated, var(--surface-bg))' }}>
    <div className="flex justify-between items-center mb-4">
      <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{title}</h3>
      {actions}
    </div>
    {children}
  </div>
);

const Grid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
);

const Field: React.FC<{ label: string; full?: boolean; children: React.ReactNode }> = ({ label, full, children }) => (
  <div className={full ? 'sm:col-span-2' : ''}>
    <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>{label}</label>
    {children}
  </div>
);

const inputStyle: React.CSSProperties = {
  background: 'var(--input-bg, var(--surface-bg))',
  border: '1px solid var(--surface-border)',
  color: 'var(--text-primary)',
  borderRadius: '8px',
  padding: '6px 10px',
  fontSize: '14px',
  width: '100%',
};

const Input: React.FC<{
  value: string; onChange: (v: string) => void; type?: string; placeholder?: string;
}> = ({ value, onChange, type = 'text', placeholder }) => (
  <input type={type} value={value} placeholder={placeholder}
    onChange={e => onChange(e.target.value)} style={inputStyle} />
);

const Textarea: React.FC<{
  value: string; onChange: (v: string) => void; rows?: number; placeholder?: string;
}> = ({ value, onChange, rows = 3, placeholder }) => (
  <textarea value={value} placeholder={placeholder} rows={rows}
    onChange={e => onChange(e.target.value)}
    style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} />
);

const Select: React.FC<{
  value: string | number; onChange: (v: string) => void; children: React.ReactNode;
}> = ({ value, onChange, children }) => (
  <select value={value} onChange={e => onChange(e.target.value)} style={inputStyle}>
    {children}
  </select>
);

const PriceInput: React.FC<{ valueCents: number; onChange: (cents: number) => void; }> = ({ valueCents, onChange }) => {
  const [text, setText] = useState<string>(centsToInput(valueCents));
  const [isEditing, setIsEditing] = useState(false);
  useEffect(() => { if (!isEditing) setText(centsToInput(valueCents)); }, [valueCents, isEditing]);
  return (
    <input type="text" inputMode="decimal" value={text} style={inputStyle}
      onFocus={e => { setIsEditing(true); e.target.select(); }}
      onChange={e => {
        const raw = e.target.value; setText(raw);
        if (raw === '' || raw === '-') { onChange(0); return; }
        const cleaned = raw.replace(/[^0-9,.\-]/g, '').replace(',', '.');
        const value = parseFloat(cleaned);
        if (!isNaN(value)) onChange(Math.round(value * 100));
      }}
      onBlur={() => { setIsEditing(false); setText(centsToInput(valueCents)); }} />
  );
};

const IntegerInput: React.FC<{ value: number; onChange: (n: number) => void; min?: number; }> = ({ value, onChange, min = 1 }) => {
  const [text, setText] = useState<string>(String(value));
  const [isEditing, setIsEditing] = useState(false);
  useEffect(() => { if (!isEditing) setText(String(value)); }, [value, isEditing]);
  return (
    <input type="text" inputMode="numeric" value={text} style={inputStyle}
      onFocus={e => { setIsEditing(true); e.target.select(); }}
      onChange={e => {
        const raw = e.target.value; setText(raw);
        if (raw === '') { onChange(min); return; }
        const n = parseInt(raw.replace(/[^0-9-]/g, ''), 10);
        if (!isNaN(n)) onChange(Math.max(min, n));
      }}
      onBlur={() => { setIsEditing(false); setText(String(value)); }} />
  );
};

export default InvoiceDetail;
